import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BookOutlinedIcon from "@mui/icons-material/BookOutlined";
import CycloneIcon from "@mui/icons-material/Cyclone";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VolumeOffOutlinedIcon from "@mui/icons-material/VolumeOffOutlined";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LetterWheel, { buildWheelTiles, lettersForWheel, shuffleWheelTiles } from "../components/LetterWheel";
import PlayBackground from "../components/PlayBackground";
import ClueBanner from "../components/ClueBanner";
import WordWheelIntermissionCompleteDialog from "../components/WordWheelIntermissionCompleteDialog";
import WordWheelDictionarySheet from "../components/WordWheelDictionarySheet";
import WordRevealBurst from "../effect/WordRevealBurst";
import useWordRevealCelebrate, {
    burstOriginsForGrid,
    celebrateCellSx,
} from "../effect/useWordRevealCelebrate";
import {
    buildCellWordNumbers,
    buildClueMapFromDisplayClue,
    buildDisplayGrid,
    buildWordClueNumbers,
    buildWordToNumberMap,
    findHintStartCandidates,
    findWordsAtCell,
    formatCellWordNumberLabel,
    normalizeWord,
    parseWordPositions,
    puzzleCellKeys,
    resolveWordWheelGridSize,
    usedPuzzleBounds,
} from "../lib/gridReveal";
import { GRID_CREAM, wordNumberBadgeTone } from "../lib/gridTheme";
import { formatWordWheelPlayDuration, WORD_WHEEL_HINT_COST } from "../lib/points";
import WordWheelApi from "../lib/api";
import { resolveScreenshotPlayColors } from "../lib/theme";
import { WORD_WHEEL_SCREENSHOT_SCENES } from "../screenshotScenes";

/** Prefer core gameplay scene chrome (theme) for capture. */
const RECORDING_SCENE =
    WORD_WHEEL_SCREENSHOT_SCENES.find((s) => s.slug === "02") ?? WORD_WHEEL_SCREENSHOT_SCENES[0];

/** App background — ad resource c5687949-c613-4e20-bea7-ae01d3a079ad. */
const RECORDING_BACKGROUND =
    "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/ad/iphone/background/c5687949-c613-4e20-bea7-ae01d3a079ad.jpg";

/** Published puzzle for the 1080p content recording (Payday Words). */
const RECORDING_PUZZLE_ID = "636ff613-a448-443a-850f-131134653a58";

const CLUE_PLACEHOLDER = "Tap a numbered cell to see the clue";
const DEMO_COINS = 45;
const DEMO_CREDITS = 3;
/** Room for corner number badges that sit half outside the board (Android BOARD_INSET). */
const BOARD_INSET = 12;

function parseWords(wordsInUse) {
    if (!wordsInUse) return [];
    return wordsInUse.split("\n").map((w) => w.trim()).filter(Boolean);
}

function TwoCoinsIcon({ size = 18, color = "#facc15" }) {
    return (
        <Box
            component="svg"
            width={size}
            height={size}
            viewBox="0 0 512 512"
            aria-hidden
            sx={{ display: "block", flexShrink: 0 }}
        >
            <path
                fill={color}
                d="M264.4 95.01c-35.6-.06-80.2 11.19-124.2 34.09C96.27 152 61.45 182 41.01 211.3c-20.45 29.2-25.98 56.4-15.92 75.8 10.07 19.3 35.53 30.4 71.22 30.4 35.69.1 80.29-11.2 124.19-34 44-22.9 78.8-53 99.2-82.2 20.5-29.2 25.9-56.4 15.9-75.8-10.1-19.3-35.5-30.49-71.2-30.49zm91.9 70.29c-3.5 15.3-11.1 31-21.8 46.3-22.6 32.3-59.5 63.8-105.7 87.8-46.2 24.1-93.1 36.2-132.5 36.2-18.6 0-35.84-2.8-50.37-8.7l10.59 20.4c10.08 19.4 35.47 30.5 71.18 30.5 35.7 0 80.3-11.2 124.2-34.1 44-22.8 78.8-52.9 99.2-82.2 20.4-29.2 26-56.4 15.9-75.7zm28.8 16.8c11.2 26.7 2.2 59.2-19.2 89.7-18.9 27.1-47.8 53.4-83.6 75.4 11.1 1.2 22.7 1.8 34.5 1.8 49.5 0 94.3-10.6 125.9-27.1 31.7-16.5 49.1-38.1 49.1-59.9 0-21.8-17.4-43.4-49.1-59.9-16.1-8.4-35.7-15.3-57.6-20zm106.7 124.8c-10.2 11.9-24.2 22.4-40.7 31-35 18.2-82.2 29.1-134.3 29.1-21.2 0-41.6-1.8-60.7-5.2-23.2 11.7-46.5 20.4-68.9 26.1 1.2.7 2.4 1.3 3.7 2 31.6 16.5 76.4 27.1 125.9 27.1s94.3-10.6 125.9-27.1c31.7-16.5 49.1-38.1 49.1-59.9z"
            />
        </Box>
    );
}

/** Multicolor chest — matches android `PiTreasureChest`. */
function TreasureChestIcon({ size = 22 }) {
    return (
        <Box
            component="svg"
            width={size}
            height={size}
            viewBox="0 0 64 64"
            aria-hidden
            sx={{ display: "block", flexShrink: 0 }}
        >
            <path
                fill="#92400E"
                d="M10 32h44v18a8 8 0 0 1-8 8H18a8 8 0 0 1-8-8V32z"
            />
            <path
                fill="#F5C542"
                d="M10 32v-6C10 14 19 6 32 6s22 8 22 20v6H10z"
            />
            <path fill="#78350F" d="M10 28h44v8H10z" />
            <path
                fill="#FFF7D6"
                d="M27 30h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H27a2 2 0 0 1-2-2V32a2 2 0 0 1 2-2z"
            />
            <circle cx="32" cy="36" r="2.4" fill="#78350F" />
            <path fill="#78350F" d="M31 37h2v5h-2z" />
        </Box>
    );
}

/** Wheel-preview letter circles — matches android `ClueLetterRow`. */
function ClueLetterRow({ word = "" }) {
    const letters = String(word || "").split("").filter(Boolean);
    if (letters.length === 0) return null;
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
            }}
        >
            {letters.map((letter, index) => (
                <Box
                    key={`${letter}-${index}`}
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        border: "2px solid #f59e0b",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        background:
                            "linear-gradient(165deg, #fffbeb 0%, #fde68a 55%, #fbbf24 100%)",
                        boxShadow: "0 1px 3px rgba(180, 83, 9, 0.28)",
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontSize: 20,
                            fontWeight: 800,
                            color: "#78350f",
                            lineHeight: 1,
                        }}
                    >
                        {letter}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

function WordNumberBadge({ label }) {
    const tone = wordNumberBadgeTone(label, false);
    return (
        <Box
            component="span"
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                transform: "translate(-45%, -45%)",
                minWidth: "clamp(14px, 28%, 22px)",
                minHeight: "clamp(14px, 28%, 22px)",
                px: String(label).length > 2 ? 0.4 : 0.25,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
                fontSize: "clamp(9px, 22%, 15px)",
                fontWeight: 800,
                color: tone.text,
                border: `1px solid ${tone.border}`,
                borderRadius: "5px",
                background: `linear-gradient(135deg, ${tone.colors[0]} 0%, ${tone.colors[1]} 100%)`,
                zIndex: 4,
                userSelect: "none",
                pointerEvents: "none",
                boxSizing: "border-box",
            }}
        >
            {label}
        </Box>
    );
}

/** Soft rising bubbles for the outer pillarbox fill (not the phone app). */
const PILLAR_BUBBLES = [
    { left: "3%", size: 28, delay: "0s", duration: "11s", sway: "18px", bottom: "2%" },
    { left: "8%", size: 16, delay: "1.1s", duration: "9s", sway: "-14px", bottom: "0%" },
    { left: "12%", size: 42, delay: "0.4s", duration: "14s", sway: "22px", bottom: "6%" },
    { left: "17%", size: 20, delay: "2s", duration: "10s", sway: "-16px", bottom: "3%" },
    { left: "21%", size: 32, delay: "0.2s", duration: "12s", sway: "14px", bottom: "1%" },
    { left: "78%", size: 36, delay: "1.6s", duration: "13s", sway: "-20px", bottom: "5%" },
    { left: "83%", size: 18, delay: "2.6s", duration: "9s", sway: "12px", bottom: "2%" },
    { left: "88%", size: 44, delay: "0.8s", duration: "15s", sway: "-18px", bottom: "0%" },
    { left: "92%", size: 22, delay: "3s", duration: "10s", sway: "10px", bottom: "4%" },
    { left: "96%", size: 30, delay: "1.3s", duration: "12s", sway: "-12px", bottom: "7%" },
    { left: "5%", size: 24, delay: "3.6s", duration: "11s", sway: "15px", bottom: "8%" },
    { left: "85%", size: 26, delay: "2.2s", duration: "14s", sway: "-15px", bottom: "9%" },
    { left: "10%", size: 14, delay: "4.1s", duration: "8s", sway: "11px", bottom: "3%" },
    { left: "90%", size: 38, delay: "0.7s", duration: "13s", sway: "-8px", bottom: "1%" },
];

function PillarboxMist() {
    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                inset: 0,
                overflow: "hidden",
                pointerEvents: "none",
                "@keyframes wwCaptureBubbleRise": {
                    "0%": {
                        transform: "translate3d(0, 0, 0) scale(0.75)",
                        opacity: 0,
                    },
                    "8%": { opacity: 0.75 },
                    "45%": {
                        transform: "translate3d(var(--sway), -50vh, 0) scale(1.05)",
                        opacity: 0.55,
                    },
                    "100%": {
                        transform: "translate3d(calc(var(--sway) * -0.5), -115vh, 0) scale(0.85)",
                        opacity: 0,
                    },
                },
            }}
        >
            {PILLAR_BUBBLES.map((b, i) => (
                <Box
                    key={i}
                    sx={{
                        position: "absolute",
                        left: b.left,
                        bottom: b.bottom,
                        width: b.size,
                        height: b.size,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.95) 0%, rgba(186,230,253,0.55) 38%, rgba(125,211,252,0.2) 68%, transparent 100%)",
                        border: "1.5px solid rgba(224,242,254,0.65)",
                        boxShadow:
                            "inset 0 0 8px rgba(255,255,255,0.45), 0 0 14px rgba(125,211,252,0.35)",
                        willChange: "transform, opacity",
                        "--sway": b.sway,
                        animation: `wwCaptureBubbleRise ${b.duration} linear ${b.delay} infinite`,
                    }}
                />
            ))}
        </Box>
    );
}

/**
 * Prototype-only 1920×1080 Word Wheel capture play page.
 * Full viewport dark canvas + centered 9:16 Android play UI (left/right pillarbox).
 */
export default function Recording1080pPlayPage() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [puzzle, setPuzzle] = useState(null);
    const [foundWords, setFoundWords] = useState([]);
    const [selectedIndices, setSelectedIndices] = useState([]);
    const [selectedWord, setSelectedWord] = useState(null);
    const [wheelTiles, setWheelTiles] = useState([]);
    const [dictionaryOpen, setDictionaryOpen] = useState(false);
    const [treasureOpen, setTreasureOpen] = useState(false);
    const [hintLetters, setHintLetters] = useState(() => new Map());
    const [coins, setCoins] = useState(DEMO_COINS);
    const [hintPending, setHintPending] = useState(false);
    const [shuffleSignal, setShuffleSignal] = useState(0);
    const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
    const [completionStats, setCompletionStats] = useState(null);
    const [hintCoinsSpent, setHintCoinsSpent] = useState(0);
    const [credits] = useState(DEMO_CREDITS);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const completionDialogShownRef = useRef(false);
    const playStartedAtRef = useRef(Date.now());

    const scene = RECORDING_SCENE;
    const background = RECORDING_BACKGROUND;
    const theme = scene?.theme || "random";
    const playColors = useMemo(() => resolveScreenshotPlayColors(theme), [theme]);

    const selectedIndicesRef = useRef(selectedIndices);
    const wheelTilesRef = useRef(wheelTiles);
    wheelTilesRef.current = wheelTiles;
    selectedIndicesRef.current = selectedIndices;

    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        const root = document.getElementById("root");
        const prev = {
            htmlOverflow: html.style.overflow,
            bodyOverflow: body.style.overflow,
            bodyMargin: body.style.margin,
            bodyBg: body.style.background,
            rootOverflow: root?.style.overflow,
        };
        html.style.overflow = "hidden";
        body.style.overflow = "hidden";
        body.style.margin = "0";
        body.style.background = "#0f172a";
        if (root) root.style.overflow = "hidden";
        return () => {
            html.style.overflow = prev.htmlOverflow;
            body.style.overflow = prev.bodyOverflow;
            body.style.margin = prev.bodyMargin;
            body.style.background = prev.bodyBg;
            if (root) root.style.overflow = prev.rootOverflow || "";
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await WordWheelApi.fetchPublishedById(RECORDING_PUZZLE_ID);
                if (data?.code === "NO_DATA") {
                    throw new Error(`No puzzle found for id ${RECORDING_PUZZLE_ID}`);
                }
                if (!cancelled) {
                    setPuzzle(data);
                    setFoundWords([]);
                    setSelectedIndices([]);
                    setSelectedWord(null);
                    setHintLetters(new Map());
                    setCoins(DEMO_COINS);
                    setHintCoinsSpent(0);
                    setCompletionDialogOpen(false);
                    setCompletionStats(null);
                    completionDialogShownRef.current = false;
                    playStartedAtRef.current = Date.now();
                    setDictionaryOpen(false);
                    setTreasureOpen(false);
                }
            } catch (e) {
                if (!cancelled) setError(e?.message || "Failed to load puzzle for recording");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const targetWords = useMemo(
        () => parseWords(puzzle?.wordsInUse).map(normalizeWord),
        [puzzle],
    );
    const baseWheelLetters = useMemo(
        () => lettersForWheel(targetWords, puzzle?.lettersGrid, puzzle?.details),
        [targetWords, puzzle],
    );
    const wordPositions = useMemo(
        () => parseWordPositions(puzzle?.filledCoordinates),
        [puzzle?.filledCoordinates],
    );
    const {
        celebratingCellKeys,
        celebrateMode,
        revealBurstId,
        orderIndex,
        staggerMs,
        triggerWordRevealEffect,
    } = useWordRevealCelebrate(wordPositions);
    const puzzleCells = useMemo(() => puzzleCellKeys(wordPositions), [wordPositions]);
    const gridSize = useMemo(() => resolveWordWheelGridSize(puzzle), [puzzle]);
    const gridBounds = useMemo(
        () => usedPuzzleBounds(puzzleCells, gridSize),
        [puzzleCells, gridSize],
    );
    const visibleRows = Math.max(1, gridBounds.maxRow - gridBounds.minRow + 1);
    const visibleCols = Math.max(1, gridBounds.maxCol - gridBounds.minCol + 1);
    const burstOrigins = useMemo(
        () => burstOriginsForGrid(celebratingCellKeys, gridSize, gridBounds),
        [celebratingCellKeys, gridSize, gridBounds],
    );
    const displayGrid = useMemo(
        () => buildDisplayGrid(foundWords, wordPositions, hintLetters, gridSize),
        [foundWords, wordPositions, hintLetters, gridSize],
    );
    const hintedCellKeys = useMemo(() => new Set(hintLetters.keys()), [hintLetters]);
    const hintCandidates = useMemo(
        () => findHintStartCandidates(puzzle?.filledCoordinates, foundWords, hintedCellKeys),
        [puzzle?.filledCoordinates, foundWords, hintedCellKeys],
    );
    const puzzleComplete = foundWords.length >= targetWords.length && targetWords.length > 0;
    const canUseHint = !puzzleComplete
        && hintCandidates.length > 0
        && coins >= WORD_WHEEL_HINT_COST
        && !hintPending;
    const hintOnlyCells = useMemo(() => {
        const keys = new Set();
        hintLetters.forEach((_, key) => {
            let fromFound = false;
            for (const w of foundWords) {
                const positions = wordPositions[w];
                if (positions?.some((p) => `${p.row},${p.col}` === key)) {
                    fromFound = true;
                    break;
                }
            }
            if (!fromFound) keys.add(key);
        });
        return keys;
    }, [hintLetters, foundWords, wordPositions]);
    const cellWordNumbers = useMemo(
        () => buildCellWordNumbers(puzzle?.filledCoordinates),
        [puzzle?.filledCoordinates],
    );
    const { wordToNumber: clueWordToNumber, cellToNumbers } = useMemo(
        () => buildWordClueNumbers(puzzle?.filledCoordinates),
        [puzzle?.filledCoordinates],
    );
    const wordToNumber = useMemo(
        () => (clueWordToNumber.size > 0
            ? clueWordToNumber
            : buildWordToNumberMap(puzzle?.filledCoordinates, cellWordNumbers)),
        [clueWordToNumber, puzzle?.filledCoordinates, cellWordNumbers],
    );
    const clueMap = useMemo(
        () => buildClueMapFromDisplayClue(puzzle?.displayClue),
        [puzzle?.displayClue],
    );

    useEffect(() => {
        setWheelTiles(buildWheelTiles(baseWheelLetters, puzzle?.id || "recording-wheel"));
    }, [puzzle?.id, baseWheelLetters]);

    const setSelection = useCallback((updater) => {
        setSelectedIndices((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            selectedIndicesRef.current = next;
            return next;
        });
    }, []);

    const submitWord = useCallback((wordRaw) => {
        const word = normalizeWord(wordRaw);
        if (word.length < 3 || !targetWords.includes(word)) {
            setSelectedIndices([]);
            return;
        }
        if (foundWords.includes(word)) {
            triggerWordRevealEffect(word, "already");
            setSelectedIndices([]);
            setSelectedWord(word);
            return;
        }
        const next = [...foundWords, word];
        setFoundWords(next);
        setSelectedIndices([]);
        setSelectedWord(word);
        triggerWordRevealEffect(word, "new");

        const puzzleCompleteNow = next.length >= targetWords.length && targetWords.length > 0;
        if (puzzleCompleteNow && !completionDialogShownRef.current) {
            completionDialogShownRef.current = true;
            const finishedAt = Date.now();
            // Demo reward for recording capture (no play session / catalog).
            const demoEarned = Math.max(0, 12 + targetWords.length * 3 - hintCoinsSpent);
            setCompletionStats({
                durationLabel: formatWordWheelPlayDuration(playStartedAtRef.current, finishedAt),
                scoreCoins: demoEarned,
                hintCoinsSpent,
            });
            // Let the last-word celebrate land before the modal.
            window.setTimeout(() => setCompletionDialogOpen(true), 1000);
        }
    }, [targetWords, foundWords, triggerWordRevealEffect, hintCoinsSpent]);

    const handleDragEnd = useCallback(() => {
        const word = selectedIndicesRef.current
            .map((i) => wheelTilesRef.current[i]?.letter || "")
            .join("");
        if (word.length >= 3) {
            submitWord(word);
        }
    }, [submitWord]);

    const handleCellClick = useCallback((row, col) => {
        const matches = findWordsAtCell(puzzle?.filledCoordinates, row, col, cellWordNumbers);
        if (matches.length === 0) return;
        setSelectedWord(matches[0].word);
    }, [puzzle?.filledCoordinates, cellWordNumbers]);

    const handleShuffleLetters = useCallback(() => {
        setSelectedIndices([]);
        setShuffleSignal((n) => n + 1);
    }, []);

    const applyWheelShuffle = useCallback(() => {
        setWheelTiles((prev) =>
            shuffleWheelTiles(
                prev.length ? prev : buildWheelTiles(baseWheelLetters, puzzle?.id || "recording-wheel"),
            ),
        );
    }, [baseWheelLetters, puzzle?.id]);

    const handleHint = useCallback(() => {
        if (!canUseHint) return;
        const pick = hintCandidates[Math.floor(Math.random() * hintCandidates.length)];
        if (!pick) return;
        setHintPending(true);
        setCoins((prev) => Math.max(0, prev - WORD_WHEEL_HINT_COST));
        setHintCoinsSpent((prev) => prev + WORD_WHEEL_HINT_COST);
        setHintLetters((prev) => {
            const next = new Map(prev);
            next.set(pick.key, pick.letter);
            return next;
        });
        setSelectedWord(pick.word);
        setHintPending(false);
    }, [canUseHint, hintCandidates]);

    const selectedWheelWord = useMemo(
        () => selectedIndices.map((i) => wheelTiles[i]?.letter || "").join(""),
        [selectedIndices, wheelTiles],
    );
    const dictionaryWord = selectedWord || (selectedWheelWord.length >= 3 ? selectedWheelWord : "") || "";
    const dictionaryWordRevealed = Boolean(
        dictionaryWord && foundWords.includes(normalizeWord(dictionaryWord)),
    );

    const selectedWordNumber = selectedWord ? wordToNumber.get(selectedWord) ?? null : null;
    const selectedClue = selectedWord ? (clueMap.get(selectedWord) || "") : "";
    const selectedWordCells = useMemo(() => {
        if (!selectedWord || !wordPositions[selectedWord]) return new Set();
        return new Set(wordPositions[selectedWord].map((p) => `${p.row},${p.col}`));
    }, [selectedWord, wordPositions]);

    const wheelColors = useMemo(
        () => ({
            ...playColors,
            surface: playColors.letterBg,
            border: playColors.letterBorder,
            textOnSurface: playColors.letterText,
        }),
        [playColors],
    );

    const toolbarButtonSx = {
        width: 34,
        height: 34,
        minWidth: 34,
        minHeight: 34,
        p: 0.25,
        bgcolor: playColors.toolBtnBg,
        border: `1.5px solid ${playColors.borderStrong}`,
        boxShadow: playColors.shadowSm,
        color: playColors.toolIcon,
        "&:hover": {
            bgcolor: playColors.toolBtnBg,
        },
        "&.Mui-disabled": {
            opacity: 0.55,
            bgcolor: playColors.toolBtnBg,
            color: playColors.toolIcon,
            border: `1.5px solid ${playColors.borderStrong}`,
        },
    };

    const coinColor = "#facc15";
    const clueText = selectedWord
        ? `${selectedWordNumber != null ? `${selectedWordNumber}. ` : ""}${selectedClue || "No clue available for this word."}`
        : CLUE_PLACEHOLDER;

    const stageSx = {
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        bgcolor: "#0f172a",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        boxSizing: "border-box",
    };

    if (error) {
        return (
            <Box sx={{ ...stageSx, p: 4 }}>
                <Box
                    aria-hidden
                    sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}
                >
                    <Box sx={{ position: "absolute", inset: 0, opacity: 0.4 }}>
                        <PlayBackground background={background} theme={theme} />
                        <PillarboxMist />
                    </Box>
                </Box>
                <Typography color="error" variant="h6" sx={{ position: "relative", zIndex: 1 }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    if (loading || !puzzle) {
        return (
            <Box sx={stageSx}>
                <Box
                    aria-hidden
                    sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}
                >
                    <Box sx={{ position: "absolute", inset: 0, opacity: 0.4 }}>
                        <PlayBackground background={background} theme={theme} />
                        <PillarboxMist />
                    </Box>
                </Box>
                <CircularProgress size={48} sx={{ color: "#38bdf8", position: "relative", zIndex: 1 }} />
            </Box>
        );
    }

    return (
        <Box sx={stageSx} data-ww-capture="1080p-play">
            {/* Pillarbox fill only — does not replace the phone app background */}
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    zIndex: 0,
                }}
            >
                <Box sx={{ position: "absolute", inset: 0, opacity: 0.4 }}>
                    <PlayBackground background={background} theme={theme} />
                    <PillarboxMist />
                </Box>
            </Box>
            {/*
              9:16 phone column — height fills the viewport; width follows aspect-ratio.
              On 1920×1080 that is ~608×1080, centered with equal left/right space.
            */}
            <Box
                sx={{
                    height: "100%",
                    aspectRatio: "9 / 16",
                    maxWidth: "100%",
                    position: "relative",
                    zIndex: 1,
                    overflow: "hidden",
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    boxSizing: "border-box",
                }}
            >
                <PlayBackground background={background} theme={theme} />

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        px: 1.75,
                        pt: 2.5,
                        pb: 2,
                        boxSizing: "border-box",
                        overflow: "visible",
                    }}
                >
                    {/* Android header: back | Level | credit chip */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "44px 1fr auto",
                            alignItems: "center",
                            flexShrink: 0,
                            mb: 1.25,
                            gap: 0.75,
                        }}
                    >
                        <IconButton
                            aria-label="Back"
                            sx={{
                                width: 44,
                                height: 44,
                                bgcolor: "rgba(255,255,255,0.94)",
                                color: "#0b3d36",
                                pointerEvents: "none",
                                "&:hover": { bgcolor: "rgba(255,255,255,0.94)" },
                            }}
                        >
                            <ArrowBackIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                        <Typography
                            sx={{
                                fontSize: 28,
                                fontWeight: 800,
                                letterSpacing: "-0.3px",
                                lineHeight: 1.1,
                                textAlign: "center",
                                color: "#ffffff",
                                textShadow: "0 1px 5px rgba(0,0,0,0.8)",
                            }}
                        >
                            {`Level ${puzzle?.puzzleLevel ?? puzzle?.mainJourneyLevel ?? "—"}`}
                        </Typography>
                        <Box
                            aria-label="Credits"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1.25,
                                py: 0.75,
                                borderRadius: "14px",
                                bgcolor: "rgba(255,255,255,0.94)",
                                pointerEvents: "none",
                            }}
                        >
                            <AutoAwesomeIcon sx={{ fontSize: 15, color: "#0b3d36" }} />
                            <Typography
                                component="span"
                                sx={{ fontSize: 15, fontWeight: 800, color: "#0b3d36", lineHeight: 1 }}
                            >
                                {credits}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Cropped cream board — matches Android PuzzleGrid + GRID_CREAM */}
                    <Box
                        sx={{
                            flex: "1 1 42%",
                            minHeight: 0,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            containerType: "size",
                            overflow: "visible",
                            p: `${BOARD_INSET}px`,
                            boxSizing: "border-box",
                        }}
                    >
                        <Box
                            sx={{
                                width: `min(100cqw, calc(100cqh * ${visibleCols} / ${visibleRows}))`,
                                aspectRatio: `${visibleCols} / ${visibleRows}`,
                                position: "relative",
                                display: "grid",
                                gridTemplateColumns: `repeat(${visibleCols}, minmax(0, 1fr))`,
                                gridTemplateRows: `repeat(${visibleRows}, minmax(0, 1fr))`,
                                gap: "4px",
                                overflow: "visible",
                            }}
                        >
                            {Array.from({ length: visibleRows }, (_, rowOffset) =>
                                Array.from({ length: visibleCols }, (_, colOffset) => {
                                    const row = gridBounds.minRow + rowOffset;
                                    const col = gridBounds.minCol + colOffset;
                                    const letter = displayGrid[row]?.[col] || "";
                                    const cellKey = `${row},${col}`;
                                    const isPuzzleCell = puzzleCells.has(cellKey);
                                    const isRevealed = Boolean(letter);
                                    const isHintRevealed = isRevealed && hintOnlyCells.has(cellKey);
                                    const isSelected = selectedWordCells.has(cellKey);
                                    const wordNumberLabel = formatCellWordNumberLabel(
                                        cellToNumbers.get(cellKey) ?? cellWordNumbers.get(cellKey),
                                    );
                                    const isCelebrating = celebratingCellKeys.has(cellKey);
                                    const celebrateSx = celebrateCellSx({
                                        isCelebrating,
                                        celebrateMode,
                                        order: orderIndex.get(cellKey),
                                        staggerMs,
                                    });
                                    const fillColor = !isPuzzleCell
                                        ? "transparent"
                                        : isSelected
                                            ? GRID_CREAM.selectedBg
                                            : isHintRevealed
                                                ? GRID_CREAM.hintBg
                                                : isRevealed
                                                    ? GRID_CREAM.revealedBg
                                                    : GRID_CREAM.cellBg;
                                    const borderColor = !isPuzzleCell
                                        ? "transparent"
                                        : isSelected
                                            ? GRID_CREAM.selectedBorder
                                            : isHintRevealed
                                                ? GRID_CREAM.hintBorder
                                                : GRID_CREAM.cellBorder;
                                    const borderWidth = isPuzzleCell
                                        ? (isSelected
                                            ? GRID_CREAM.selectedBorderWidth
                                            : GRID_CREAM.cellBorderWidth)
                                        : 0;
                                    const letterColor = isSelected
                                        ? GRID_CREAM.selectedText
                                        : isHintRevealed
                                            ? GRID_CREAM.hintText
                                            : GRID_CREAM.revealedText;
                                    return (
                                        <Box
                                            key={
                                                isCelebrating
                                                    ? `${cellKey}-c-${revealBurstId}`
                                                    : cellKey
                                            }
                                            onClick={
                                                isPuzzleCell
                                                    ? () => handleCellClick(row, col)
                                                    : undefined
                                            }
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                aspectRatio: "1 / 1",
                                                position: "relative",
                                                fontSize: "clamp(12px, 9cqmin, 22px)",
                                                fontWeight: 800,
                                                fontFamily:
                                                    '"Segoe UI", system-ui, -apple-system, sans-serif',
                                                borderRadius: "6px",
                                                cursor: isPuzzleCell ? "pointer" : "default",
                                                bgcolor: fillColor,
                                                color: isRevealed ? letterColor : "transparent",
                                                border: isPuzzleCell
                                                    ? `${borderWidth}px solid ${borderColor}`
                                                    : "none",
                                                boxShadow: "none",
                                                minWidth: 0,
                                                minHeight: 0,
                                                lineHeight: 1,
                                                overflow: "visible",
                                                ...celebrateSx,
                                            }}
                                        >
                                            {wordNumberLabel && isPuzzleCell ? (
                                                <WordNumberBadge label={wordNumberLabel} />
                                            ) : null}
                                            {letter}
                                        </Box>
                                    );
                                }),
                            )}
                            {celebrateMode === "new" ? (
                                <WordRevealBurst
                                    origins={burstOrigins}
                                    burstId={revealBurstId}
                                />
                            ) : null}
                        </Box>
                    </Box>

                    <Box sx={{ mb: 1, position: "relative", overflow: "visible", zIndex: 3 }}>
                        <ClueBanner
                            clueText={clueText}
                            hasClue={Boolean(selectedWord)}
                            androidPlay
                            flush
                            overlay={
                                selectedWheelWord ? (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            px: 1.5,
                                            borderRadius: "22px",
                                            bgcolor: "rgba(255,255,255,0.72)",
                                            pointerEvents: "none",
                                            zIndex: 3,
                                        }}
                                    >
                                        <ClueLetterRow word={selectedWheelWord} />
                                    </Box>
                                ) : null
                            }
                        />
                        {!puzzleComplete ? (
                            <IconButton
                                aria-label="Reveal one letter"
                                size="small"
                                sx={{
                                    position: "absolute",
                                    top: -2,
                                    right: -2,
                                    width: 32,
                                    height: 32,
                                    bgcolor: "rgba(255,255,255,0.96)",
                                    border: "1.5px solid #E8943A",
                                    boxShadow: "0 2px 3px rgba(139, 90, 43, 0.22)",
                                    color: "#3F2A1A",
                                    zIndex: 6,
                                    "&:hover": { bgcolor: "rgba(255,255,255,0.96)" },
                                }}
                            >
                                <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        ) : null}
                    </Box>

                    {/* Wheel + side tools (Android PlayScreen) */}
                    <Box
                        sx={{
                            flex: "1 1 38%",
                            minHeight: 140,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.75,
                            width: "100%",
                            overflow: "visible",
                        }}
                    >
                        <Stack
                            spacing={0.75}
                            sx={{
                                width: 44,
                                flexShrink: 0,
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "visible",
                                zIndex: 5,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.4,
                                    pointerEvents: "none",
                                    filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.55))",
                                }}
                            >
                                <TwoCoinsIcon size={18} color={coinColor} />
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 800,
                                        lineHeight: 1,
                                        color: coins < WORD_WHEEL_HINT_COST ? "#fecaca" : coinColor,
                                        textShadow: "0 1px 2px rgba(0,0,0,0.55)",
                                    }}
                                >
                                    {coins}
                                </Typography>
                            </Box>
                            <IconButton
                                size="small"
                                aria-label="Hint"
                                onClick={handleHint}
                                disabled={!canUseHint}
                                sx={toolbarButtonSx}
                            >
                                {hintPending ? (
                                    <CircularProgress size={14} sx={{ color: "inherit" }} />
                                ) : (
                                    <LightbulbOutlinedIcon sx={{ fontSize: 18, color: "inherit" }} />
                                )}
                            </IconButton>
                            <IconButton
                                size="small"
                                aria-label="Bonus words"
                                onClick={() => setTreasureOpen(true)}
                                sx={toolbarButtonSx}
                            >
                                <TreasureChestIcon size={22} />
                            </IconButton>
                        </Stack>

                        <Box
                            sx={{
                                flex: "1 1 auto",
                                minWidth: 0,
                                minHeight: 0,
                                height: "100%",
                                maxWidth: "min(280px, calc(100% - 104px))",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                containerType: "size",
                                overflow: "visible",
                            }}
                        >
                            {wheelTiles.length > 0 ? (
                                <Box
                                    sx={{
                                        width: "min(100cqw, 100cqh)",
                                        aspectRatio: "1 / 1",
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                    }}
                                >
                                    <LetterWheel
                                        tiles={wheelTiles}
                                        selectedIndices={selectedIndices}
                                        onSelectionChange={setSelection}
                                        onDragEnd={handleDragEnd}
                                        onShuffle={applyWheelShuffle}
                                        shuffleSignal={shuffleSignal}
                                        colors={wheelColors}
                                        fill
                                    />
                                </Box>
                            ) : null}
                        </Box>

                        <Stack
                            spacing={0.75}
                            sx={{
                                width: 44,
                                flexShrink: 0,
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "visible",
                                zIndex: 5,
                            }}
                        >
                            <IconButton
                                size="small"
                                aria-label="Dictionary"
                                onClick={() => setDictionaryOpen(true)}
                                disabled={!dictionaryWord}
                                sx={toolbarButtonSx}
                            >
                                <BookOutlinedIcon sx={{ fontSize: 18, color: "inherit" }} />
                            </IconButton>
                            <IconButton
                                size="small"
                                aria-label="Shuffle letters"
                                onClick={handleShuffleLetters}
                                disabled={wheelTiles.length < 2}
                                sx={toolbarButtonSx}
                            >
                                <CycloneIcon sx={{ fontSize: 18, color: "inherit" }} />
                            </IconButton>
                            <IconButton
                                size="small"
                                aria-label={soundEnabled ? "Mute sound" : "Unmute sound"}
                                onClick={() => setSoundEnabled((prev) => !prev)}
                                sx={toolbarButtonSx}
                            >
                                {soundEnabled ? (
                                    <VolumeUpOutlinedIcon sx={{ fontSize: 18, color: "inherit" }} />
                                ) : (
                                    <VolumeOffOutlinedIcon sx={{ fontSize: 18, color: "inherit" }} />
                                )}
                            </IconButton>
                        </Stack>
                    </Box>
                </Box>

                <WordWheelDictionarySheet
                    open={dictionaryOpen}
                    onClose={() => setDictionaryOpen(false)}
                    word={dictionaryWord}
                    wordRevealed={dictionaryWordRevealed}
                    language={puzzle?.language || "english"}
                    colors={playColors}
                    contained
                />

                <WordWheelIntermissionCompleteDialog
                    open={completionDialogOpen}
                    onClose={() => setCompletionDialogOpen(false)}
                    durationLabel={completionStats?.durationLabel}
                    scoreCoins={completionStats?.scoreCoins ?? 0}
                    hintCoinsSpent={completionStats?.hintCoinsSpent ?? 0}
                    contained
                />
            </Box>

            <Dialog
                open={treasureOpen}
                onClose={() => setTreasureOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: "rgba(255,255,255,0.96)",
                        borderRadius: 3,
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
                    Found words
                </DialogTitle>
                <DialogContent sx={{ pt: 0, pb: 2.5 }}>
                    {foundWords.length === 0 ? (
                        <Typography sx={{ color: "text.secondary", fontStyle: "italic" }}>
                            No words found yet. Swipe the wheel to discover them.
                        </Typography>
                    ) : (
                        <Stack spacing={0.75}>
                            {foundWords.map((word) => (
                                <Box
                                    key={word}
                                    component="button"
                                    type="button"
                                    onClick={() => {
                                        setSelectedWord(word);
                                        setTreasureOpen(false);
                                        setDictionaryOpen(true);
                                    }}
                                    sx={{
                                        display: "block",
                                        width: "100%",
                                        m: 0,
                                        py: 1,
                                        px: 1.5,
                                        borderRadius: 2,
                                        border: `1px solid ${playColors.borderStrong}`,
                                        bgcolor: playColors.successSoft,
                                        color: playColors.successText,
                                        fontWeight: 700,
                                        fontSize: 16,
                                        letterSpacing: "0.06em",
                                        textAlign: "left",
                                        cursor: "pointer",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {word}
                                </Box>
                            ))}
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
